/**
 * Browser side of zero-loss lead capture (14 Oct 2026).
 *
 * - A failed send (the server's "Could not save", or no answer on the
 *   network) is retried automatically after LEAD_RETRY_DELAYS_MS, each time
 *   with a fresh Turnstile token got the normal way (a token is single-use).
 * - What the visitor typed is kept in this tab's sessionStorage while a send
 *   has not gone through, and put back in the form if the page reloads. It
 *   never leaves the browser except in the form's own send, and it is
 *   cleared as soon as a send succeeds. This is the visitor's own form data,
 *   not tracking, so it does not depend on cookie consent.
 */
import { mainPhone } from "@/lib/site-core";

/** Wait before the automatic resends (2 resends after the first try). */
export const LEAD_RETRY_DELAYS_MS = [2_000, 5_000];

export const LEAD_CAPTURE_ERROR = `We couldn't send your details. Please try again, or call ${mainPhone}.`;

export const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * The message to show after the last retry. Our own capture failures and
 * network errors get the short message with the phone number; anything else
 * (a field the server rejected, for example) keeps the server's text.
 */
export function finalLeadError(err: unknown): string {
  const message = err instanceof Error ? err.message : "";
  if (!message || isCaptureFailure(message)) return LEAD_CAPTURE_ERROR;
  return message;
}

export function isCaptureFailure(message: string): boolean {
  return /could not save|failed to fetch|networkerror|network request failed|load failed|fetch failed|timed out|internal server error|\b50[0-9]\b|unexpected (end|token)/iu.test(
    message,
  );
}

const DRAFT_PREFIX = "rh_lead_draft:";
/** Fields never kept: bot-check values and hidden honeypots. */
const SKIP = /turnstile|token|company_site|^cf-|^hp$/iu;

/** Keep the form's text fields in sessionStorage. Never throws. */
export function saveLeadDraft(key: string, form: HTMLFormElement | null): void {
  if (!form || typeof window === "undefined") return;
  try {
    const out: Record<string, string> = {};
    for (const el of Array.from(form.elements)) {
      const field = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (!field.name || SKIP.test(field.name)) continue;
      if (field instanceof HTMLInputElement) {
        // Consent boxes are never ticked for the visitor: they tick them again.
        if (["file", "password", "hidden", "submit", "button", "checkbox", "radio"].includes(field.type)) continue;
      }
      if (field.value) out[field.name] = field.value;
    }
    window.sessionStorage.setItem(DRAFT_PREFIX + key, JSON.stringify({ at: Date.now(), fields: out }));
  } catch {
    // Storage blocked: the fields are still on the page.
  }
}

/**
 * Put a kept draft back into empty fields. Returns true when something was
 * restored. Drafts older than a day are dropped.
 */
export function restoreLeadDraft(key: string, form: HTMLFormElement | null): boolean {
  if (!form || typeof window === "undefined") return false;
  try {
    const raw = window.sessionStorage.getItem(DRAFT_PREFIX + key);
    if (!raw) return false;
    const draft = JSON.parse(raw) as { at: number; fields: Record<string, string> };
    if (!draft?.fields || Date.now() - draft.at > 24 * 60 * 60_000) {
      window.sessionStorage.removeItem(DRAFT_PREFIX + key);
      return false;
    }
    let restored = false;
    for (const [name, value] of Object.entries(draft.fields)) {
      const fields = form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
        `[name="${CSS.escape(name)}"]`,
      );
      for (const field of Array.from(fields)) {
        if (field instanceof HTMLInputElement && ["checkbox", "radio", "hidden", "file"].includes(field.type)) continue;
        if (!field.value) {
          field.value = value;
          restored = true;
        }
      }
    }
    return restored;
  } catch {
    return false;
  }
}

export function clearLeadDraft(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(DRAFT_PREFIX + key);
  } catch {
    // ignore
  }
}

/**
 * Turnstile helper for forms that must send a valid token (website forms):
 * resets the widget and resolves with the next token, or null after
 * `timeoutMs` (an interactive challenge the visitor has not finished).
 */
export function createTokenWaiter() {
  let waiter: ((t: string | null) => void) | null = null;
  return {
    /** Call from the widget's onToken. */
    onToken(token: string) {
      const w = waiter;
      waiter = null;
      w?.(token);
    },
    next(reset: (() => void) | null | undefined, timeoutMs = 8_000): Promise<string | null> {
      return new Promise((resolve) => {
        const timer = setTimeout(() => {
          waiter = null;
          resolve(null);
        }, timeoutMs);
        waiter = (t) => {
          clearTimeout(timer);
          resolve(t);
        };
        reset?.();
      });
    },
  };
}
